/**
 * CSS generation utilities for table styling
 */

import type { BreakpointRowConfig } from "../../types";
import {
  generateColumnClassCSS,
  generateColumnIndexCSS,
  generateRowCSS,
  generateTableClassCSS,
  generateCellCSS,
  generateCellClassCSS,
} from "../../utils/tableUtils";

// Re-export helper functions from tableStyleHelpers
export { hashCode, convertToSingleQuoteAttributes } from "./tableStyleHelpers";

// Helper function to regenerate all CSS from data attributes for a specific table
export function regenerateAllCSS(finalHtml: string, tableId: string): string {
  let allCSS = "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(finalHtml, "text/html");
  const table = doc.querySelector(`table[data-table-id="${tableId}"]`);
  
  if (!table) return allCSS;
  
  // 1. Regenerate TABLE CSS from individual data-width-* attributes
  const tableClass = (table.getAttribute("class") || "").split(" ").find(c => c.startsWith("table-style-"));
  
  // Parse per-breakpoint widths from individual attributes
  const tableWidths: Record<string, Record<string, unknown>> = {};
  const breakpoints = ["xs", "sm", "md", "lg", "xl"];
  breakpoints.forEach(bp => {
    const widthAttr = table.getAttribute(`data-width-${bp}`);
    if (widthAttr) {
      const parts = widthAttr.split(":");
      tableWidths[bp] = {
        t: parts[0] || "percent",    // type
        v: parts[1] || "100",         // value
        a: parts[2] || "left",     // alignment
        hs: parts[3] === "1",      // horizontalScroll
        rg: parts[4] ? parseInt(parts[4]) : 0, // rowGap
      };
    }
  });
  
  // Generate CSS for table if it has a table-style-* class
  // (Inline styles handle the default case without a class)
  if (tableClass) {
    const borderRadius = parseInt(table.getAttribute("data-border-radius") || "0");
    const borderColor = table.getAttribute("data-border-color") || "#000000";
    const borderWidth = parseInt(table.getAttribute("data-border-width") || "1");
    
    allCSS += generateTableClassCSS(tableClass, {
      tw: tableWidths,
      tbr: borderRadius,
      tbc: borderColor,
      tbw: borderWidth,
    }, tableId);
  }
  
  // 2. Regenerate ALL COLUMN CSS from individual cell data-col-width-* attributes
  const rows = table.querySelectorAll("tr");
  const processedColClasses = new Set<string>();
  const processedColIndices = new Set<number>();
  
  if (rows.length > 0) {
    const firstRowCells = rows[0].querySelectorAll("td, th");
    firstRowCells.forEach((cell, colIndex) => {
      const cellEl = cell as HTMLElement;
      const colClass = (cellEl.getAttribute("class") || "").split(" ").find(c => c.startsWith("col-style-"));
      
      // Parse per-breakpoint column widths from individual attributes
      const colStyles: Record<string, Record<string, unknown>> = {};
      const breakpoints = ["xs", "sm", "md", "lg", "xl"];
      let hasWidthData = false;
      
      breakpoints.forEach(bp => {
        const widthAttr = cellEl.getAttribute(`data-col-width-${bp}`);
        if (widthAttr) {
          hasWidthData = true;
          const parts = widthAttr.split(":");
          colStyles[bp] = {
            wt: parts[0] || "auto",  // widthType
            wv: parts[1] || "",       // widthValue
          };
        }
      });
      
      // Generate CSS if we have width data
      if (hasWidthData && Object.keys(colStyles).length > 0) {
        if (colClass && !processedColClasses.has(colClass)) {
          // Use class-based selector
          processedColClasses.add(colClass);
          allCSS += generateColumnClassCSS(colClass, colStyles, tableId);
        } else if (!colClass && !processedColIndices.has(colIndex)) {
          // Use column-index-based selector (fallback when no class)
          processedColIndices.add(colIndex);
          allCSS += generateColumnIndexCSS(colIndex, colStyles, tableId);
        }
      }
    });
  }
  
  // 3. Regenerate ALL ROW CSS
  // Get data-all-row-settings directly from the table element (not via regex on full HTML)
  const allRowSettingsAttr = table.getAttribute('data-all-row-settings');
  if (allRowSettingsAttr) {
    try {
      const allSavedRowSettings = JSON.parse(allRowSettingsAttr.replace(/&quot;/g, '"')) as Record<string, { rowIndex: number; styles: Record<string, Partial<BreakpointRowConfig>> }>;
      for (const [rowIdxStr, savedRowData] of Object.entries(allSavedRowSettings)) {
        const savedRowIdx = parseInt(rowIdxStr, 10);
        allCSS += generateRowCSS(savedRowIdx, savedRowData.styles, tableId);
      }
    } catch {
      // Ignore parse errors
    }
  }
  
  // 4. Regenerate ALL CELL CSS from table-level data-cell-styles
  // Get data-cell-styles directly from the table element (not via regex on full HTML)
  // to ensure we get the correct styles for THIS specific table
  const cellStylesAttr = table.getAttribute('data-cell-styles');
  if (cellStylesAttr) {
    try {
      const cellStylesData = JSON.parse(cellStylesAttr.replace(/&quot;/g, '"').replace(/&apos;/g, "'")) as Record<string, { s: Record<string, Record<string, unknown>> }>;
      for (const [cellClass, cellData] of Object.entries(cellStylesData)) {
        if (cellData.s) {
          allCSS += generateCellClassCSS(cellClass, cellData.s, tableId);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }
  
  // Also handle legacy data-cell-id for backwards compatibility
  // Scope to cells within THIS table only
  const cells = table.querySelectorAll('td[data-cell-id], th[data-cell-id]');
  cells.forEach((cell) => {
    const cellEl = cell as HTMLElement;
    const existingCellId = cellEl.getAttribute('data-cell-id');
    const cellSettingsAttr = cellEl.getAttribute('data-cell-settings');
    if (existingCellId && cellSettingsAttr) {
      try {
        const cellData = JSON.parse(cellSettingsAttr.replace(/&quot;/g, '"').replace(/&apos;/g, "'"));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellCSS(existingCellId, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
  });
  
  // Also handle legacy data-cell-class with data-cell-settings on each cell
  // Scope to cells within THIS table only
  const cellsWithClass = table.querySelectorAll('td[data-cell-class], th[data-cell-class]');
  const processedLegacyClasses = new Set<string>();
  cellsWithClass.forEach((cell) => {
    const cellEl = cell as HTMLElement;
    const cellClass = cellEl.getAttribute('data-cell-class');
    const cellSettingsAttr = cellEl.getAttribute('data-cell-settings');
    if (cellClass && cellSettingsAttr && !processedLegacyClasses.has(cellClass)) {
      processedLegacyClasses.add(cellClass);
      try {
        const cellData = JSON.parse(cellSettingsAttr.replace(/&quot;/g, '"').replace(/&apos;/g, "'"));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellClassCSS(cellClass, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
  });
  
  // Fallback: Find cells with cell-style-* classes but no data-cell-styles on table
  // Scope to cells within THIS table only
  const allCells = table.querySelectorAll('td, th');
  const processedStyleClasses = new Set<string>();
  allCells.forEach((cell) => {
    const cellEl = cell as HTMLElement;
    const classAttr = cellEl.getAttribute('class') || '';
    const cellStyleMatch = classAttr.match(/\b(cell-style-\w+)\b/);
    if (!cellStyleMatch) return;
    
    const cellClass = cellStyleMatch[1];
    if (processedStyleClasses.has(cellClass)) return;
    processedStyleClasses.add(cellClass);
    if (allCSS.includes(`.${cellClass}`)) return;
    
    const inlineStyle = cellEl.getAttribute('style') || '';
    const styleRules: string[] = [];
    const bgMatch = inlineStyle.match(/background-color:\s*([^;]+)/i);
    if (bgMatch) styleRules.push(`background-color:${bgMatch[1].trim()}!important`);
    const colorMatch = inlineStyle.match(/(?<!background-)color:\s*([^;]+)/i);
    if (colorMatch) styleRules.push(`color:${colorMatch[1].trim()}!important`);
    const paddingMatch = inlineStyle.match(/padding:\s*([^;]+)/i);
    if (paddingMatch) styleRules.push(`padding:${paddingMatch[1].trim()}!important`);
    const borderColorMatch = inlineStyle.match(/border-color:\s*([^;]+)/i);
    if (borderColorMatch) styleRules.push(`border-color:${borderColorMatch[1].trim()}!important`);
    const borderStyleMatch = inlineStyle.match(/border-style:\s*([^;]+)/i);
    if (borderStyleMatch) styleRules.push(`border-style:${borderStyleMatch[1].trim()}!important`);
    const borderWidthMatch = inlineStyle.match(/border-width:\s*([^;]+)/i);
    if (borderWidthMatch) styleRules.push(`border-width:${borderWidthMatch[1].trim()}!important`);
    
    if (styleRules.length > 0) {
      allCSS += `table[data-table-id="${tableId}"] td.${cellClass},table[data-table-id="${tableId}"] th.${cellClass}{${styleRules.join(';')}}`;
    }
  });
  
  return allCSS;
}

// Helper function to generate CSS for all tables in the HTML
export function generateCSSForAllTables(htmlString: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const allTables = doc.querySelectorAll("table[data-table-id]");
  let allStyleTags = "";
  
  allTables.forEach((tbl) => {
    const tblId = tbl.getAttribute("data-table-id");
    if (tblId) {
      const tableCSS = regenerateAllCSS(htmlString, tblId);
      if (tableCSS) {
        allStyleTags += `<style data-table-style="${tblId}">${tableCSS}</style>`;
      }
    }
  });
  
  return allStyleTags;
}

